import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { LeadLabelType } from "@signalist/shared-types";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";

export type LeadLabelColor = "green" | "red" | "blue" | "yellow" | "grey" | "orange";

export const SEGMENT_COLORS: Record<LeadLabelColor, string> = {
	green: "bg-green-500",
	red: "bg-status-error",
	blue: "bg-blue-action",
	yellow: "bg-type-safety",
	grey: "bg-gray-400",
	orange: "bg-status-warning",
};

/** Maps each server-computed label type (see @signalist/shared-types) to its stripe/dot color. */
export const LEAD_LABEL_COLOR: Record<LeadLabelType, LeadLabelColor> = {
	IN_ACTIVE_CAMPAIGN: "red",
	CONNECTED_ON_LINKEDIN: "blue",
	PREVIOUS_OUTREACH: "yellow",
	ON_OTHER_LISTS: "grey",
	NEW_LEAD: "green",
};

/** Order the labels are listed in the legend tooltip (independent of per-row priority). */
const LEGEND_ORDER: LeadLabelType[] = [
	"NEW_LEAD",
	"IN_ACTIVE_CAMPAIGN",
	"CONNECTED_ON_LINKEDIN",
	"PREVIOUS_OUTREACH",
	"ON_OTHER_LISTS",
];

export const LeadColorLabels: FunctionComponent<{ row: ListPerson; thin?: boolean }> = ({
	row,
	thin = false,
}) => {
	const { t } = useTranslation();
	const labels = row.labels ?? [];

	if (labels.length === 0) {
		return null;
	}

	const primaryColor = LEAD_LABEL_COLOR[labels[0]];

	const legendContent = (
		<div className="flex flex-col gap-1">
			<div className="text-xs font-semibold text-text-meta mb-1">
				{t("inbox.leadLabels.stateLegend")}
			</div>
			{LEGEND_ORDER.map((labelType) => (
				<div key={labelType} className="flex flex-row items-center gap-2">
					<div
						className={`w-1 h-3 rounded-xs shrink-0 ${SEGMENT_COLORS[LEAD_LABEL_COLOR[labelType]]}`}
					/>
					<span className="text-xs">{t(`inbox.leadLabels.types.${labelType}`)}</span>
				</div>
			))}
		</div>
	);

	return (
		<div
			style={{ position: "absolute", bottom: 0, top: 0, left: 0, width: thin ? 3 : 5, zIndex: 10 }}
		>
			<Tooltip content={legendContent} direction="bottom" className="!block w-full h-full">
				<div className={SEGMENT_COLORS[primaryColor]} style={{ width: "100%", height: "100%" }} />
			</Tooltip>
		</div>
	);
};
